import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "../../styles/aerosTokens";

// Estilos compartilhados pelas abas de Configurações (Horários, Finalização,
// Empresas, Planos, Ajuda, Whitelabel), extraídos do mockup
// docs/design/aeros-dark/mockups/Telas Aeros Dark - Kanban, Campanhas e
// Configurações.dc.html, seção "Configurações · demais abas". Cada aba usa
// makeStyles próprio só para o que é específico dela.
const useAerosSettingsStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  const borderGhost = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.14)";
  return {
    // ---- cabeçalho da aba: título 15/600 + subtítulo 12 + ações à direita ----
    sectionHead: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginBottom: 14,
      flexWrap: "wrap",
    },
    sectionTitle: {
      fontSize: 15,
      fontWeight: 600,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    sectionSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      lineHeight: 1.4,
    },
    sectionActions: {
      marginLeft: "auto",
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    microLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      marginBottom: 10,
    },
    // ---- botões ----
    ghostButton: {
      border: `1px solid ${borderGhost}`,
      borderRadius: 9,
      padding: "6px 12px",
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      "& .MuiButton-startIcon": { marginRight: 5 },
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 15 },
    },
    gradientButton: {
      borderRadius: 10,
      padding: "8px 14px",
      fontSize: 13,
      fontWeight: 700,
      whiteSpace: "nowrap",
      "& .MuiButton-startIcon": { marginRight: 5 },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    gradientButtonSmall: {
      borderRadius: 9,
      padding: "7px 14px",
      fontSize: 12,
      fontWeight: 700,
      whiteSpace: "nowrap",
    },
    // ---- cards ----
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      padding: "14px 16px",
      minWidth: 0,
    },
    cardTight: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "12px 14px",
      minWidth: 0,
    },
    cardTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    cardDesc: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      lineHeight: 1.4,
    },
    fieldLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: 5,
      display: "block",
    },
    // ---- inputs (fundo sidebar, borda 0.1, raio 9/10) ----
    input: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: theme.palette.aeros.sidebar,
        fontSize: 13,
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: borderInput },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.aeros.borderStrong,
      },
      "& .MuiOutlinedInput-input": { padding: "9px 12px" },
      "& .MuiOutlinedInput-multiline": { padding: 0 },
      "& .MuiSelect-root": { padding: "9px 12px" },
      "& .MuiSelect-icon": { color: theme.palette.text.secondary },
    },
    inputSmall: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 8,
        backgroundColor: theme.palette.aeros.sidebar,
        fontSize: 12.5,
        fontVariantNumeric: "tabular-nums",
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: borderInput },
      "& .MuiOutlinedInput-input": { padding: "7px 10px" },
    },
    searchPill: {
      flex: 1,
      minWidth: 200,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      padding: "4px 12px",
      fontSize: 13,
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      gap: 8,
      "& .MuiSvgIcon-root": { fontSize: 17, color: theme.palette.text.secondary },
      "& input::placeholder": { color: theme.palette.text.secondary, opacity: 1 },
    },
    // ---- tabela em CSS grid (card surface, header caps 10.5/700) ----
    tableCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      overflow: "hidden",
    },
    tableHead: {
      display: "grid",
      alignItems: "center",
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      backgroundColor: isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.02)",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
    },
    tableRow: {
      display: "grid",
      alignItems: "center",
      fontSize: 12.5,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}`,
      "&:last-child": { borderBottom: "none" },
      "&:hover": { backgroundColor: "rgba(160,68,255,0.04)" },
    },
    headCell: { padding: "10px 6px", minWidth: 0 },
    headCellFirst: { paddingLeft: 14 },
    headCellLast: { paddingRight: 14, textAlign: "right" },
    cell: { padding: "9px 6px", minWidth: 0 },
    cellFirst: { paddingLeft: 14 },
    cellActions: {
      paddingRight: 14,
      display: "flex",
      justifyContent: "flex-end",
      gap: 5,
    },
    emptyRow: {
      padding: "28px 0",
      textAlign: "center",
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
    },
    // ---- pills / chips ----
    pill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: 10.5,
      fontWeight: 600,
      borderRadius: 99,
      padding: "2px 9px",
      whiteSpace: "nowrap",
    },
    pillDot: {
      width: 5,
      height: 5,
      borderRadius: "50%",
      backgroundColor: "currentColor",
      flexShrink: 0,
    },
    pillSuccess: {
      color: stateText(theme, theme.palette.success.main, "success"),
      backgroundColor: alpha(theme.palette.success.main, 0.1),
    },
    pillWarning: {
      color: stateText(theme, theme.palette.warning.main, "warning"),
      backgroundColor: alpha(theme.palette.warning.main, 0.1),
    },
    pillError: {
      color: stateText(theme, theme.palette.error.main, "error"),
      backgroundColor: alpha(theme.palette.error.main, 0.1),
    },
    pillNeutral: {
      color: theme.palette.text.secondary,
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
    },
    chipPurple: {
      display: "inline-block",
      fontSize: 10.5,
      fontWeight: 600,
      color: stateText(theme, "#C08BFF", "purple"),
      backgroundColor: "rgba(160,68,255,0.12)",
      borderRadius: 5,
      padding: "2px 8px",
      whiteSpace: "nowrap",
    },
    chipNeutral: {
      display: "inline-block",
      fontSize: 10,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
      borderRadius: 5,
      padding: "1px 7px",
      whiteSpace: "nowrap",
    },
    // ---- icon buttons de linha (26/28px, borda 0.08, hover ciano/vermelho) ----
    iconButton: {
      width: 26,
      height: 26,
      borderRadius: 7,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.secondary,
      padding: 0,
      "&:hover": {
        color: theme.palette.secondary.main,
        borderColor: alpha(AEROS.cyan, 0.4),
        backgroundColor: "transparent",
      },
      "& .MuiSvgIcon-root": { fontSize: 14 },
    },
    iconButtonLg: {
      width: 28,
      height: 28,
      borderRadius: 8,
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
    iconButtonDanger: {
      "&:hover": {
        color: theme.palette.error.main,
        borderColor: alpha(theme.palette.error.main, 0.4),
        backgroundColor: "transparent",
      },
    },
    // ---- toggle 36×20 (gradiente vem do override global MuiSwitch) ----
    toggleSwitch: {
      flexShrink: 0,
      width: 36,
      height: 20,
      padding: 0,
      "& .MuiSwitch-switchBase": {
        padding: 2,
        "&.Mui-checked": { transform: "translateX(16px)" },
      },
      "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "none" },
      "& .MuiSwitch-track": { borderRadius: 99, opacity: 1 },
    },
    toggleSwitchSmall: {
      flexShrink: 0,
      width: 32,
      height: 18,
      padding: 0,
      "& .MuiSwitch-switchBase": {
        padding: 2,
        "&.Mui-checked": { transform: "translateX(14px)" },
      },
      "& .MuiSwitch-thumb": { width: 14, height: 14, boxShadow: "none" },
      "& .MuiSwitch-track": { borderRadius: 99, opacity: 1 },
    },
    toggleRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    // ---- grids ----
    grid2: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 10,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    grid3: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 10,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    kpiValue: {
      fontSize: 20,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
      lineHeight: 1.2,
    },
    kpiLabel: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
    },
    textWarning: { color: stateText(theme, theme.palette.warning.main, "warning") },
    textSuccess: { color: stateText(theme, theme.palette.success.main, "success") },
    textError: { color: stateText(theme, theme.palette.error.main, "error") },
    textTertiary: { color: theme.palette.aeros.textTertiary },
    // ---- modal (surface2, raio 16, título 16/700, footer com divisor) ----
    dialogPaper: {
      borderRadius: 16,
      backgroundColor: theme.palette.aeros.surface2,
    },
    dialogTitle: {
      fontSize: 16,
      fontWeight: 700,
      padding: "18px 22px 8px",
    },
    dialogContent: {
      padding: "8px 22px 16px",
    },
    dialogActions: {
      padding: "12px 22px 16px",
      borderTop: `1px solid ${border}`,
      gap: 8,
    },
    // ---- botão de salvar alinhado à direita ----
    footerActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 8,
      marginTop: 14,
    },
  };
});

export default useAerosSettingsStyles;
